import { Button } from "@/components/ui/button";
import type { ModelCandidate } from "@/services/adminModelCurrency";
import ConfirmActionDialog from "./ConfirmActionDialog";
import RunTestButton from "./RunTestButton";
import TestingNote from "./TestingNote";
import { canPromote, canRunTest } from "./helpers";
import { canRetest } from "./layoutHelpers";
import { useConfirmedAction } from "./useConfirmedAction";

interface CandidateActionsProps {
  candidate: ModelCandidate;
  writable: boolean;
  /** The model this feature runs on today. */
  currentModel: string;
  /** Promote is unavailable for a reason shown elsewhere (e.g. the result is out of date). */
  promoteBlocked?: boolean;
  /** Resolve on success, reject on failure (the parent toasts). */
  onPromote: (candidateId: string) => Promise<unknown>;
  onDismiss: (candidateId: string) => Promise<unknown>;
  onRunTest: (candidateId: string) => Promise<unknown>;
}

const CLAUDE_KEY_NOTE =
  "This is a Claude model. Make sure ANTHROPIC_API_KEY is set in staging and production before the next data push.";

function promoteDescription(candidate: ModelCandidate): string {
  const base = `${candidate.model} will replace ${candidate.currentModel} for this use case. The change reaches staging and production at the next data push.`;
  return candidate.provider === "anthropic" ? `${base} ${CLAUDE_KEY_NOTE}` : base;
}

const CLOSED: ModelCandidate["status"][] = ["promoted", "dismissed"];

const CandidateActions = ({
  candidate,
  writable,
  currentModel,
  promoteBlocked = false,
  onPromote,
  onDismiss,
  onRunTest,
}: CandidateActionsProps) => {
  const promote = useConfirmedAction(() => onPromote(candidate.id));
  const dismiss = useConfirmedAction(() => onDismiss(candidate.id));

  if (CLOSED.includes(candidate.status)) return null;

  return (
    <div className="flex items-center gap-2">
      {candidate.status === "evaluating" ? (
        <TestingNote startedAt={candidate.evalStartedAt} />
      ) : null}
      {canRunTest(candidate, writable) ? (
        <RunTestButton
          label={candidate.status === "failed" ? "Run again" : "Run test"}
          onRun={() => onRunTest(candidate.id)}
        />
      ) : null}
      {canRetest(candidate, writable, currentModel) ? (
        <RunTestButton
          label={`Test against ${currentModel}`}
          onRun={() => onRunTest(candidate.id)}
        />
      ) : null}
      <Button
        size="sm"
        disabled={!writable || promoteBlocked || !canPromote(candidate)}
        onClick={() => promote.setOpen(true)}
      >
        Promote
      </Button>
      <Button size="sm" variant="ghost" disabled={!writable} onClick={() => dismiss.setOpen(true)}>
        Dismiss
      </Button>
      <ConfirmActionDialog
        open={promote.open}
        onOpenChange={promote.setOpen}
        title={`Promote ${candidate.model}?`}
        description={promoteDescription(candidate)}
        confirmLabel="Yes, promote"
        pending={promote.pending}
        onConfirm={promote.confirm}
      />
      <ConfirmActionDialog
        open={dismiss.open}
        onOpenChange={dismiss.setOpen}
        title={`Dismiss ${candidate.model}?`}
        description="It will stay hidden and won't be announced again."
        confirmLabel="Yes, dismiss"
        pending={dismiss.pending}
        onConfirm={dismiss.confirm}
      />
    </div>
  );
};

export default CandidateActions;
